import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { listActiveHomeBannersByPlacement } from "../services/homeBannersService";

const isVideoSrc = (src = "") => /\.(mp4|webm|ogg)(\?.*)?$/i.test(src);

export default function BannersSliderFirestore({
  placement = "mid",
  take = 10,
  autoplay = true,
  defaultInterval = 4500,
  loopVideos = false,
  className = "",
  sectionTitle = "", // optionnel
}) {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const list = await listActiveHomeBannersByPlacement(placement, { take });
        if (!cancelled) setBanners(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setBanners([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [placement, take]);

  const list = useMemo(() => {
    return (Array.isArray(banners) ? banners : [])
      .map((b) => {
        const src = b.src || "";
        const isVideo = b.type === "video" || isVideoSrc(src);
        return {
          ...b,
          src,
          isVideo,
          interval: Number(b.interval || defaultInterval),
          fit: b.fit || "cover",
          objectPosition: b.objectPosition || "center",
          ctaText: b.ctaText || "Voir",
          ctaTo: b.ctaTo || "/catalogue",
        };
      })
      .filter((b) => !!b.src);
  }, [banners, defaultInterval]);

  const count = list.length;
  const current = count ? list[active % count] : null;

  const next = useCallback(() => { if (count) setActive((i) => (i + 1) % count); }, [count]);
  const prev = useCallback(() => { if (count) setActive((i) => (i - 1 + count) % count); }, [count]);

  // autoplay images
  useEffect(() => {
    if (!current || count <= 1) return;
    if (!autoplay || paused) return;
    if (current.isVideo) return;
    const id = setTimeout(next, current.interval || defaultInterval);
    return () => clearTimeout(id);
  }, [current, count, autoplay, paused, next, defaultInterval]);

  // video play
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (!current?.isVideo) return;

    v.muted = true;
    v.playsInline = true;

    if (!autoplay || paused) {
      try { v.pause(); } catch {}
      return;
    }
    v.play().catch(() => {});
  }, [active, current?.isVideo, autoplay, paused]);

  if (loading) return null;
  if (!count) return null;

  return (
    <section
      className={`${className}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label={`Bannières ${placement}`}
    >
      {sectionTitle && (
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h2 className="h6 m-0">{sectionTitle}</h2>
          <Link to="/catalogue" className="btn btn-sm btn-dark">Voir plus →</Link>
        </div>
      )}

      <div className="rounded-4 overflow-hidden shadow-sm position-relative bs-wrap">
        {current.isVideo ? (
          <video
            key={`${active}-${current.src}`}
            ref={videoRef}
            className="bs-media"
            src={current.src}
            poster={current.poster || ""}
            muted
            playsInline
            autoPlay={autoplay && !paused}
            loop={!!loopVideos}
            onEnded={() => !loopVideos && next()}
          />
        ) : (
          <img
            key={`${active}-${current.src}`}
            className="bs-media"
            src={current.src}
            alt={current.title || "Bannière"}
            loading="eager"
          />
        )}

        <div className="bs-overlay" />

        <div className="bs-content">
          <div className="bs-card">
            {current.badge && <span className="badge text-bg-warning mb-2">{current.badge}</span>}
            {current.title && <h3 className="bs-title">{current.title}</h3>}
            {current.subtitle && <p className="bs-subtitle">{current.subtitle}</p>}
            <div className="d-flex gap-2 flex-wrap">
              <Link to={current.ctaTo} className="btn btn-dark btn-sm">
                {current.ctaText}
              </Link>
              {count > 1 && (
                <button
                  type="button"
                  className="btn btn-outline-light btn-sm"
                  onClick={() => setPaused((p) => !p)}
                >
                  {paused ? "▶ Reprendre" : "⏸ Pause"}
                </button>
              )}
            </div>
          </div>
        </div>

        {count > 1 && (
          <>
            <button className="bs-nav bs-prev" onClick={prev} aria-label="Précédent">‹</button>
            <button className="bs-nav bs-next" onClick={next} aria-label="Suivant">›</button>

            <div className="bs-dots">
              {list.map((_, i) => (
                <button
                  key={i}
                  className={`bs-dot ${i === active ? "is-active" : ""}`}
                  onClick={() => setActive(i)}
                  aria-label={`Aller à la bannière ${i + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <style>{`
        .bs-wrap{ aspect-ratio: 16/7; min-height: 220px; background:#111; }
        @media (max-width: 576px){ .bs-wrap{ aspect-ratio: 4/5; } }
        .bs-media{
          position:absolute; inset:0;
          width:100%; height:100%;
          object-fit:${current.fit || "cover"};
          object-position:${current.objectPosition || "center"};
        }
        .bs-overlay{
          position:absolute; inset:0;
          background: linear-gradient(90deg, rgba(0,0,0,.62) 0%, rgba(0,0,0,.35) 45%, rgba(0,0,0,.10) 100%);
        }
        @media (max-width: 576px){
          .bs-overlay{ background: linear-gradient(180deg, rgba(0,0,0,.22) 0%, rgba(0,0,0,.50) 55%, rgba(0,0,0,.72) 100%); }
        }
        .bs-content{ position:absolute; inset:0; display:flex; align-items:flex-end; padding:18px; }
        @media (min-width: 992px){ .bs-content{ align-items:center; padding:28px; } }
        .bs-card{ max-width:560px; color:#fff; }
        .bs-title{ font-weight:800; font-size: clamp(1.05rem, 2.6vw, 1.9rem); line-height:1.1; margin:0 0 8px 0; }
        .bs-subtitle{ margin:0 0 12px 0; opacity:.92; font-size: clamp(.9rem, 2vw, 1.05rem); }
        .bs-nav{
          position:absolute; top:50%; transform: translateY(-50%);
          width:44px; height:44px; border-radius:999px; border:0;
          background: rgba(255,255,255,.92); color:#111;
          display:grid; place-items:center; font-size:28px;
          box-shadow: 0 10px 18px rgba(0,0,0,.18);
        }
        .bs-prev{ left: 12px; }
        .bs-next{ right: 12px; }
        @media (max-width: 768px){ .bs-nav{ display:none; } }
        .bs-dots{ position:absolute; left:0; right:0; bottom:10px; display:flex; justify-content:center; gap:8px; }
        .bs-dot{ width:10px; height:10px; border-radius:999px; border:0; background: rgba(255,255,255,.55); }
        .bs-dot.is-active{ background:#fff; width:22px; }
      `}</style>
    </section>
  );
}
